import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Hourglass, Loader2, Pause, Play, Square } from 'lucide-react';
import { cn } from '@/lib/utils';
import { LoadingSpinner } from '@/components/loading-spinner';
import './run-timer-pill.css';

interface RunTimerPillProps {
  isExecutingWorkflow: boolean;
  isPaused: boolean;
  isPausing: boolean;
  isResuming: boolean;
  isStopping: boolean;
  // True while the run is parked on a clarification question. The run is
  // idle (no compute), so the pill freezes the timer and shows a "Waiting
  // for input" affordance instead of a ticking timer + dead pause button.
  isWaitingForInput?: boolean;
  workflowStartedAt: string | null;
  onPlay: () => void;
  onPause: () => void;
  onResume: () => void;
  onStop: () => void;
  playDisabled?: boolean;
  isLoading?: boolean;
  // Shared ref owned by the parent. The pill writes elapsed-ms to it on every
  // tick so the value survives unmount (e.g. closing the console sidebar), and
  // reads from it when mounting into an already-paused run to restore both the
  // display value and the totalPausedRef accumulator so resume is seamless.
  elapsedSnapshotRef?: React.MutableRefObject<number>;
}

function formatHMS(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (v: number) => String(v).padStart(2, '0');
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

export const RunTimerPill: React.FC<RunTimerPillProps> = ({
  isExecutingWorkflow,
  isPaused,
  isPausing,
  isResuming,
  isStopping,
  isWaitingForInput = false,
  workflowStartedAt,
  onPlay,
  onPause,
  onResume,
  onStop,
  playDisabled = false,
  isLoading = false,
  elapsedSnapshotRef,
}) => {
  // Seed from snapshot when remounting into any active run (paused or
  // ticking) so the display is immediately correct — no 00:00:00 flash.
  const [elapsedMs, setElapsedMs] = useState(() =>
    isExecutingWorkflow ? (elapsedSnapshotRef?.current ?? 0) : 0,
  );
  const [isExpanding, setIsExpanding] = useState(false);
  const [isCollapsing, setIsCollapsing] = useState(false);
  // Lags isExecutingWorkflow by the collapse animation's duration so the
  // pill keeps rendering (shrinking away) instead of unmounting to the
  // idle Play button the instant the run actually ends.
  const [displayExecuting, setDisplayExecuting] = useState(isExecutingWorkflow);

  const startTimeRef = useRef<number | null>(null);
  const pauseStartTimeRef = useRef<number | null>(null);
  const totalPausedRef = useRef<number>(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const prevExecutingRef = useRef(false);
  // True when this mount started while a run was already active (paused or
  // ticking). Triggers a one-time re-anchor of startTimeRef in the running
  // branch so the timer continues from the snapshot rather than from 0.
  const mountedWhileRunningRef = useRef(isExecutingWorkflow);

  // Sync start time from the backend timestamp so the timer is authoritative
  useEffect(() => {
    if (!workflowStartedAt) return;
    const normalized = /[Zz]|[+-]\d{2}:\d{2}$/.test(workflowStartedAt)
      ? workflowStartedAt
      : `${workflowStartedAt}Z`;
    const t = new Date(normalized).getTime();
    if (!isNaN(t)) {
      startTimeRef.current = t;
      totalPausedRef.current = 0;
    }
  }, [workflowStartedAt]);

  // Drive the interval — ticks when executing and actively running, freezes
  // when paused OR parked on a clarification (idle), resets when idle.
  // Waiting-for-input reuses the pause accumulator so the parked time is
  // excluded from the elapsed count and the timer doesn't jump on resume.
  useEffect(() => {
    const clearTick = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    const isFrozen = isPaused || isWaitingForInput;

    if (isExecutingWorkflow && !isFrozen) {
      if (mountedWhileRunningRef.current) {
        // First running tick after any remount (paused or ticking): re-anchor
        // startTimeRef so elapsed = snapshot + tickDelta. This avoids
        // losing accumulated pause time across remounts and prevents the
        // 0-reset when closing/opening the sidebar while running.
        const restoreMs = elapsedSnapshotRef?.current ?? 0;
        if (restoreMs > 0) {
          startTimeRef.current = Date.now() - restoreMs;
          totalPausedRef.current = 0;
        }
        mountedWhileRunningRef.current = false;
        pauseStartTimeRef.current = null;
      } else if (pauseStartTimeRef.current !== null) {
        // Normal resume from same mount: add frozen duration to the accumulator
        totalPausedRef.current += Date.now() - pauseStartTimeRef.current;
        pauseStartTimeRef.current = null;
      }
      if (startTimeRef.current === null) {
        startTimeRef.current = Date.now();
      }

      clearTick();
      intervalRef.current = setInterval(() => {
        if (startTimeRef.current !== null) {
          const elapsed = Math.max(
            0,
            Date.now() - startTimeRef.current - totalPausedRef.current,
          );
          setElapsedMs(elapsed);
          if (elapsedSnapshotRef) elapsedSnapshotRef.current = elapsed;
        }
      }, 200);

      return clearTick;
    }

    if (isExecutingWorkflow && isFrozen) {
      // Record when the freeze started so we can subtract it on resume.
      if (pauseStartTimeRef.current === null) {
        pauseStartTimeRef.current = Date.now();
      }
      clearTick();
      return;
    }

    if (displayExecuting) {
      // The run just ended but the collapse animation is still playing
      // -- keep showing the final elapsed time instead of resetting to
      // zero out from under the shrinking pill.
      clearTick();
      return;
    }

    // Idle — reset everything
    clearTick();
    startTimeRef.current = null;
    pauseStartTimeRef.current = null;
    totalPausedRef.current = 0;
    if (elapsedSnapshotRef) elapsedSnapshotRef.current = 0;
    setElapsedMs(0);
  }, [
    isExecutingWorkflow,
    isPaused,
    isWaitingForInput,
    elapsedSnapshotRef,
    displayExecuting,
  ]);

  // Trigger expand animation on new run start, and the collapse
  // animation once the run actually ends (stop confirmed, or the run
  // completed/failed) -- not the moment Stop is clicked, since that
  // signal can now take a few seconds to land and shrinking away
  // immediately would leave an empty sliver on screen for the wait.
  useEffect(() => {
    const wasExecuting = prevExecutingRef.current;
    prevExecutingRef.current = isExecutingWorkflow;

    if (!wasExecuting && isExecutingWorkflow) {
      setDisplayExecuting(true);
      setIsExpanding(true);
      const id = setTimeout(() => setIsExpanding(false), 1000);
      return () => clearTimeout(id);
    }

    if (wasExecuting && !isExecutingWorkflow) {
      setIsCollapsing(true);
      const id = setTimeout(() => {
        setIsCollapsing(false);
        setDisplayExecuting(false);
      }, 500);
      return () => clearTimeout(id);
    }
  }, [isExecutingWorkflow]);

  // ── Idle state ──────────────────────────────────────────────────────────
  if (!displayExecuting) {
    return (
      <Button
        onClick={onPlay}
        disabled={playDisabled}
        aria-label='Run workflow'
        className='text-white'
      >
        {isLoading ? <LoadingSpinner /> : <Play className='size-4' />}
        Run
      </Button>
    );
  }

  // ── Running / Paused pill ───────────────────────────────────────────────
  // h-9 + items-stretch ensures the bordered left section and the stop button
  // are both exactly 36 px tall (border-box sizing keeps borders inside).
  // When paused, run-timer-gradient-border is applied directly on this wrapper
  // via background-clip: border-box — no extra wrapper div, no size change.
  // Parked on a clarification: read as "waiting", not "running".
  const isParked = isPaused || isWaitingForInput;
  return (
    <div
      className={cn(
        'flex items-stretch h-9',
        isParked && 'run-timer-gradient-border',
      )}
    >
      <div
        className={cn(
          'flex items-center rounded-l-md bg-accent border-l border-t border-b border-border pl-3',
          isExpanding && 'run-timer-pill-expanding',
          isCollapsing && 'run-timer-pill-collapsing',
        )}
      >
        {isWaitingForInput ? (
          // Run is idle awaiting the user's answer. Primary path is to answer
          // the card, but a Pause/Resume toggle is offered: pausing here tells
          // the backend to suspend the clarification timeout so the question
          // waits indefinitely (instead of auto-resolving while the user is
          // away). The pulse is dropped while paused (static = parked).
          <>
            <span
              className={cn(
                'flex items-center gap-1.5 pr-1 text-sm text-foreground select-none whitespace-nowrap',
                !isPaused && 'run-timer-waiting-pulse',
              )}
            >
              <Hourglass className='size-3.5' />
              Waiting for input
            </span>
            <Button
              size='icon'
              variant='ghost'
              className='w-9 h-full shrink-0 rounded-none'
              onClick={isPaused ? onResume : onPause}
              disabled={isPausing || isResuming || isStopping}
              aria-label={isPaused ? 'Resume workflow' : 'Pause workflow'}
            >
              {isPausing || isResuming ? (
                <Loader2 className='size-4 animate-spin' />
              ) : isPaused ? (
                <Play className='size-4' />
              ) : (
                <Pause className='size-4' />
              )}
            </Button>
          </>
        ) : (
          <>
            {/* Timer digits */}
            <span
              className={cn(
                'text-sm font-mono tabular-nums text-foreground select-none whitespace-nowrap',
                isPaused && 'run-timer-text-blink',
              )}
            >
              {formatHMS(elapsedMs)}
            </span>

            {/* Pause / Resume button */}
            <Button
              size='icon'
              variant='ghost'
              className='w-9 h-full shrink-0 rounded-none'
              onClick={isPaused ? onResume : onPause}
              disabled={isPausing || isResuming || isStopping}
              aria-label={isPaused ? 'Resume workflow' : 'Pause workflow'}
            >
              {isPausing || isResuming ? (
                <Loader2 className='size-4 animate-spin' />
              ) : isPaused ? (
                <Play className='size-4' />
              ) : (
                <Pause className='size-4' />
              )}
            </Button>
          </>
        )}
      </div>

      {/* Stop button – shares the same h-9 row height, right corners rounded */}
      <Button
        size='icon'
        variant='destructive'
        className={cn(
          'w-9 h-full shrink-0 rounded-l-none rounded-r-md shadow-sm dark:bg-red-500',
          isParked ? 'border-none' : 'border-border',
        )}
        onClick={onStop}
        disabled={isStopping || isCollapsing}
        aria-label='Stop workflow'
      >
        {isStopping ? (
          <Loader2 className='size-4 animate-spin' />
        ) : (
          <Square className='size-4 fill-white' />
        )}
      </Button>
    </div>
  );
};
